import * as Sentry from "@sentry/nextjs"
import { Avatar, Button, Dropdown, Modal } from "flowbite-react"
import React, { useState } from "react"

import useAuth from "@hooks/useAuth"
import CustomerLoginForm from "@modules/auth/pages/CustomerLoginPage/CustomerLoginForm"
import * as CustomerServices from "@mono/essentials/services/CustomerService"
import { IDomain } from "@mono/essentials/types/UserTypes"
import { getBaseUrl } from "@mono/essentials/utils/Common"
import { useMediaQuery } from "@hooks/useMediaQuery"

interface CustomerLoginProps {
  isIntegratedPage?: boolean
}

const CustomerLogin = ({ isIntegratedPage }: CustomerLoginProps) => {
  const {
    isCustomerLoggedIn,
    loggedInCustomerDetails,
    doLogout,
    publicDomainDetails,
    token,
    selectedTenant,
  } = useAuth()
  const [isLoginEnabled, setIsLoginEnabled] = useState(false)

  const isMobile = useMediaQuery(425)

  const handleCancelLogin = () => {
    setIsLoginEnabled(false)
  }

  const onSuccessCustomerLogin = () => {
    setIsLoginEnabled(false)
  }

  const handleLogout = async () => {
    try {
      if (isCustomerLoggedIn) {
        await CustomerServices.customerSignout()
      }
      await doLogout?.()
    } catch (e) {
      Sentry.captureException(e)
    }
  }

  if (!publicDomainDetails?.enable_customer_login) {
    return <></>
  }

  return (
    <>
      {isCustomerLoggedIn ? (
        <div
          className={`flex items-center gap-4 border border-neutral-450 py-0.5 px-2 rounded-md mr-2 w-fit`}
        >
          <Dropdown
            inline
            label={
              <div className={"flex items-center"}>
                <Avatar
                  alt={"User settings"}
                  img={loggedInCustomerDetails ? (loggedInCustomerDetails.profile_image ?? "") : ""}
                  rounded
                  size={"xs"}
                />
                <div
                  className={
                    "items-center text-start text-wrap pl-2 text14Medium text-neutral-1000 md:flex"
                  }
                >
                  {loggedInCustomerDetails
                    ? `${loggedInCustomerDetails.first_name} ${loggedInCustomerDetails?.last_name}`
                    : ""}
                </div>
              </div>
            }
          >
            <Dropdown.Item
              key={"manage-account"}
              href={`${window.location.protocol}//${(publicDomainDetails as IDomain).domain}.${getBaseUrl()}/customer/login?access_token=${token}&tenant_id=${selectedTenant}`}
              target={"_blank"}
              rel={"noopener noreferrer"}
            >
              Manage My Account
            </Dropdown.Item>
            <Dropdown.Item key={"logout"} className={"text-red-600"} onClick={handleLogout}>
              Sign out
            </Dropdown.Item>
          </Dropdown>
        </div>
      ) : (
        <Button
          color={"ghost"}
          outline
          onClick={(e) => {
            e.stopPropagation()
            setIsLoginEnabled(true)
          }}
          className={`mr-2`}
          size={"xs"}
        >
          Login
        </Button>
      )}
      <Modal onClose={handleCancelLogin} show={isLoginEnabled} size={"md"}>
        <Modal.Header className={" bg-white"}>Sign in</Modal.Header>
        <Modal.Body className={`${isMobile ? "pt-4 px-4" : "pt-8 px-8"} pb-2 !bg-white`}>
          <CustomerLoginForm
            isRedirectEnabled={false}
            onSuccessCustomerLogin={onSuccessCustomerLogin}
            isIntegratedPage={isIntegratedPage}
            disableGoogleSignin={isIntegratedPage}
          />
        </Modal.Body>
      </Modal>
    </>
  )
}

export default CustomerLogin
